import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    TextField,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { confirmDialog } from "../../../../components/ConfirmDialog/confirmDialog";
import {
    deleteRateType,
    getRateTypes,
    createRateType,
    editRateType,
} from "../../../../api/apiCall";
import { useAuth } from "../../../../auth/auth";
import { toast } from "react-toastify";
import AccessForbidden from "../../../../components/Access/AccessForbidden";
import Loading from "../../../../components/Loading/Loading";

const RateTypes = () => {
    const { state } = useAuth();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [rateTypes, setRateTypes] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
    const [currentItem, setCurrentItem] = useState<any>(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [loadingPage, setLoadingPage] = useState(true);
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        isActive: true,
        shiftType: "",
    });

    const handleAddClick = () => {
        setModalMode('add');
        setFormData({ name: "", description: "", isActive: true, shiftType: "" });
        setCurrentItem(null);
        setIsModalOpen(true);
    };

    const handleEditClick = (params: any) => {
        setModalMode('edit');
        setCurrentItem(params.row);
        setFormData({
            name: params.row.name || "",
            description: params.row.description || "",
            isActive: params.row.isActive ?? true,
            shiftType: params.row.shiftType ?? "",
        });
        setIsModalOpen(true);
    };

    const handleDialogClose = () => {
        setIsModalOpen(false);
        setFormData({ name: "", description: "", isActive: true, shiftType: "" });
        setCurrentItem(null);
    };

    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            toast.error('Rate type name cannot be empty');
            return;
        }

        if (!formData.description.trim()) {
            toast.error('Rate type description cannot be empty');
            return;
        }

        if (modalMode === 'add') {
            const response = await createRateType(state.user?.token, formData);
            if (response) {
                toast.success('Rate type added successfully');
                fetchRateTypes();
                handleDialogClose();
            } else {
                toast.error('Something went wrong!');
            }
        } else {
            if (currentItem) {
                const response = await editRateType(state.user?.token, {
                    id: currentItem.id,
                    ...formData
                });
                if (response) {
                    toast.success('Rate type updated successfully');
                    fetchRateTypes();
                    handleDialogClose();
                } else {
                    toast.error('Failed to update rate type');
                }
            }
        }
    };

    const handleDelete = async (id: any) => {
        try {
            const response: any = await deleteRateType(state.user?.token, id);
            if (response?.forbidden) {
                toast.warn('You don\'t have permission to delete this role');
                return;
            }
            if (response) {
                toast.success('Role deleted successfully');
                fetchRateTypes();
            }
        } catch (error) {
            console.error("Failed to delete role:", error);
            toast.error('Failed to delete role');
        }
    };

    const fetchRateTypes = async () => {
        try {
            setLoading(true);
            const response = await getRateTypes(state.user?.token);
            if (response) {
                setRateTypes(response.data);
            } else {
                setIsAdmin(true);
            }
        } catch (error) {
            console.error("Failed to fetch rate types:", error);
        } finally {
            setLoading(false);
            setLoadingPage(false);
        }
    };

    useEffect(() => {
        fetchRateTypes();
    }, []);

    if (loadingPage) {
        return <Loading />;
    }

    if (isAdmin) {
        return <AccessForbidden />;
    }

    const columns: any = [
        {
            field: "name",
            headerName: "Name",
            flex: 1
        },
        {
            field: "description",
            headerName: "Description",
            flex: 1,
            headerAlign: 'center',
            align: 'center'
        },
        {
            field: "payRateCount",
            headerName: "Rate Count",
            flex: 1,
            headerAlign: 'center',
            align: 'center'
        },
        {
            field: "shiftType",
            headerName: "Shift Type",
            flex: 1,
            headerAlign: 'center',
            align: 'center'
        },
        {
            field: "actions",
            headerName: "Actions",
            flex: 0.8,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => (
                <div>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => handleEditClick(params)}
                        style={{ marginRight: 8, backgroundColor: "#6c757d", boxShadow: "none" }}
                    >
                        Edit
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        size="small"
                        style={{ boxShadow: "none" }}
                        onClick={() =>
                            confirmDialog({
                                title: `Are you sure you want to delete ${params.row.name}?`,
                                onOk: () => handleDelete(params.row.id),
                                content: "Deleting this rate type will also permanently delete all pay and charge rates associated with it. This action cannot be undone.",
                                okButtonProps: { danger: true },
                                okText: 'YES',
                                cancelText: 'NO',
                                centered: true
                            })
                        }
                    >
                        Delete
                    </Button>
                </div>
            )
        }
    ];

    return (
        <Box
            sx={{
                p: 1,
                height: '100%',
                minHeight: '100vh'
            }}>
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    borderRadius: '16px',
                    backgroundColor: '#ffffff',
                    maxWidth: '1400px',
                    margin: '0 auto'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        mb: 3
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={handleAddClick}
                        style={{ boxShadow: 'none' }}
                    >
                        Add Rate Type
                    </Button>
                </Box>
                <Box
                    sx={{
                        width: "100%",
                        height: '600px',
                        margin: "0 auto",
                        "@media (max-width: 800px)": {
                            overflowX: "auto",
                            margin: 1,
                            minWidth: "95%",
                        },
                    }}
                >
                    <DataGrid
                        rows={rateTypes}
                        columns={columns}
                        disableRowSelectionOnClick
                        loading={loading}
                        sx={{
                            "@media (max-width: 700px)": {
                                minWidth: "100%",
                            },
                            "& .MuiDataGrid-columnHeader": {
                                color: "#333",
                                fontWeight: "bold",
                                fontSize: 15,
                                backgroundColor: '#f0f0f0',
                            },
                            "& .MuiDataGrid-columnHeaderTitle": {
                                fontWeight: "bold",
                            },
                            "& .MuiDataGrid-row:hover": {
                                backgroundColor: "#f1f1f1",
                            },
                        }}
                    />
                </Box>
            </Paper>

            <Dialog
                open={isModalOpen}
                onClose={handleDialogClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {modalMode === 'add' ? 'Add Rate Type' : 'Edit Rate Type'}
                </DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr",
                            gap: 2,
                            mt: 1
                        }}>
                        <TextField
                            label="Name"
                            fullWidth
                            size="small"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                        />
                        <TextField
                            select
                            size="small"
                            label="Shift Type"
                            value={formData.shiftType}
                            onChange={(e) => setFormData({ ...formData, shiftType: e.target.value })}
                        >
                            <MenuItem value={0}>Day Shift</MenuItem>
                            <MenuItem value={1}>Night Shift</MenuItem>
                            <MenuItem value={2}>Weekend</MenuItem>
                            <MenuItem value={3}>Holiday</MenuItem>
                        </TextField>
                        <TextField
                            label="Description"
                            fullWidth
                            size="small"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            required
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} color="primary" variant="contained" style={{ boxShadow: 'none' }}>
                        {modalMode === 'add' ? 'Add' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RateTypes;
